import { useCallback, useEffect, useRef, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  api,
  type EtteremSor,
  type KodAllapot,
  type SzerzAttekintes,
  type SzerzAttekintesSor,
  type SzerzodesLista,
  type SzerzodesNezet,
} from '../api.ts';
import { ft } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { Mezo, SzamMezo } from '../reszek/Mezo.tsx';
import { ablakotBezar, ablakotNyit } from '../reszek/dokumentum-ablak.ts';
import { LevelKuldoPanel, type LevelKeres } from '../reszek/LevelKuldes.tsx';
import { SZERZODES_CSS, szerzodesLap, szerzodesNyomtatas, szerzodesPdf } from '../reszek/szerzodes-dokumentum.ts';

/**
 * Szerződések — önálló nyilvántartás. (szerzodesek-atepites.md, 4.)
 *
 * Három fül:
 *  - **Áttekintés** — partnerenként egy sor, a MO és FO állapotával egymás
 *    mellett. A napi használat nézete.
 *  - **MO** és **FO** — szerződésenként egy sor, a hatósági beadáshoz és az
 *    exporthoz; a két típus adatai nem keverednek.
 *
 * Az újrakötés új szerződés, a régi „újrakötve" marad — így három év múlva
 * is látszik, kinek mikor volt szerződése.
 */

type Ful = 'AT' | 'MO' | 'FO';
type Tipus = 'MO' | 'FO';

const ALLAPOT_NEVEK: Record<SzerzodesNezet['allapot'], string> = {
  elo: 'élő',
  lejart: 'lejárt',
  ujra: 'újrakötve',
  lezart: 'lezárva',
  helyszini: 'helyszíni',
  kikuldott: 'kiküldve, aláírásra vár',
};

/** „élő · 45 nap", „lejárt · 12 napja" */
function allapotSzoveg(s: SzerzodesNezet): string {
  const nev = ALLAPOT_NEVEK[s.allapot];
  if (s.nap === null) return nev;
  if (s.allapot === 'elo') return `${nev} · ${s.nap} nap`;
  if (s.allapot === 'lejart') return `${nev} · ${-s.nap} napja`;
  return nev;
}

function allapotOsztaly(s: SzerzodesNezet): string | undefined {
  if (s.allapot === 'lejart') return 'lejart';
  // A kiküldött szerződés „nézd meg" állapot: aláírásra vár.
  if (s.allapot === 'kikuldott') return 'lejar-hamarosan';
  if (s.allapot === 'elo' && s.nap !== null && s.nap <= 60) return 'lejar-hamarosan';
  return undefined;
}

function SzamCella({ s }: { s: SzerzodesNezet }) {
  return s.szam ? (
    <>{s.szam}</>
  ) : (
    <span className="halvany" title="A partnerkódok kiosztása után kapja meg a végleges számát">
      ideiglenes
    </span>
  );
}

const hibaSzoveg = (h: unknown, alap: string) => (h instanceof Error ? h.message : alap);
const ma = () => new Date().toISOString().slice(0, 10);

type Urlap =
  | { fajta: 'uj'; tipus: Tipus; etterem: { id: number; nev: string } | null }
  | { fajta: 'helyszini'; tipus: Tipus };

export function Szerzodesek({
  kezdoSzuro,
  onKarton,
}: {
  kezdoSzuro?: string;
  /** A partnerkarton megnyitása (a névre kattintva). */
  onKarton?: (etterem_id: number) => void;
}) {
  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_torzs');
  const kuldhet = useMuvelet('email_kuldes');
  const [ful, setFul] = useState<Ful>('AT');
  const [kereses, setKereses] = useState('');
  const [keresesK, setKeresesK] = useState('');
  const [szuro, setSzuro] = useState<string>(kezdoSzuro ?? 'mind');
  const [att, setAtt] = useState<SzerzAttekintes | null>(null);
  const [lista, setLista] = useState<SzerzodesLista | null>(null);
  const [urlap, setUrlap] = useState<Urlap | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [kuldendo, setKuldendo] = useState<LevelKeres | null>(null);

  /** A szerződés elkészül, és a küldő panel nyílik meg vele. */
  async function kuldesElokeszites(sz: SzerzodesNezet) {
    try {
      const lap = await szerzodesLap(sz.id);
      setKuldendo({
        tipus: 'szerzodes',
        cim: lap.cim,
        etterem_id: lap.etterem_id,
        tabla: 'szerzodes',
        rekord_id: sz.id,
        adatok: {
          szerzodesszam: sz.szam ?? '',
          partner: lap.partner,
          tipus: sz.tipus,
          kotes: sz.kotes ?? '',
          lejarat: sz.lejarat ?? '',
        },
        html: lap.html,
        fajlnev: `szerzodes-${sz.szam ?? sz.id}`,
      });
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A szerződés nem készült el.');
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setKeresesK(kereses), 220);
    return () => clearTimeout(t);
  }, [kereses]);

  /** Csak a legfrissebb kérés válasza kerüljön a képernyőre. */
  const sorszam = useRef(0);

  const betolt = useCallback(async () => {
    const sajat = ++sorszam.current;
    try {
      const p = new URLSearchParams();
      if (keresesK.trim()) p.set('q', keresesK.trim());
      p.set('szuro', szuro);
      if (ful === 'AT') {
        const v = await api.get<SzerzAttekintes>(`/api/szerzodes?${p}`);
        if (sajat === sorszam.current) setAtt(v);
      } else {
        p.set('tipus', ful);
        const v = await api.get<SzerzodesLista>(`/api/szerzodes/lista?${p}`);
        if (sajat === sorszam.current) setLista(v);
      }
      if (sajat === sorszam.current) setHiba(null);
    } catch (h) {
      if (sajat === sorszam.current) setHiba(hibaSzoveg(h, 'Nem sikerült betölteni.'));
    }
  }, [ful, keresesK, szuro]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const adat = ful === 'AT' ? att : lista;
  const o = adat?.osszesites;

  function fulValtas(f: Ful) {
    setFul(f);
    setSzuro('mind');
  }

  const ment = useCallback(
    async (id: number, mezo: string, ertek: unknown) => {
      await api.patch(`/api/szerzodes/${id}/mezo/${mezo}`, { ertek });
      await betolt();
    },
    [betolt],
  );

  async function nyomtat(s: SzerzodesNezet) {
    try {
      await szerzodesNyomtatas(s.id);
    } catch (h) {
      setHiba(hibaSzoveg(h, 'A dokumentum nem készült el.'));
    }
  }

  /** PDF-ben — ha a nyomtató a böngészőből nyomtatva akadozik. (2026-10-06) */
  async function pdf(s: SzerzodesNezet) {
    try {
      await szerzodesPdf(s.id);
    } catch (h) {
      setHiba(hibaSzoveg(h, 'A PDF nem készült el.'));
    }
  }

  async function lezar(s: SzerzodesNezet) {
    const indok = window.prompt(
      'Miért zárod le ezt a szerződést?\n(pl. nem kívánt újraszerződni)',
      'nem kívánt újraszerződni',
    );
    if (indok === null) return;
    try {
      await api.post(`/api/szerzodes/${s.id}/lezaras`, { indok });
      setUzenet(`Lezárva: ${s.szam ?? s.nev} — ${indok || 'nem kívánt újraszerződni'}.`);
      await betolt();
    } catch (h) {
      setHiba(hibaSzoveg(h, 'A lezárás nem sikerült.'));
    }
  }

  /**
   * Tévesen rögzített szerződés törlése. (2026-09-18)
   *
   * A lezárás erre nem jó: az úgy maradna a listában, mintha tényleg lett
   * volna ilyen szerződés. A törölt sor adatai a naplóba kerülnek.
   */
  async function torol(s: SzerzodesNezet) {
    const indok = window.prompt(
      `Törlöd ezt a szerződést?\n\n${s.szam ?? 'ideiglenes számú'} — ${s.nev}\n` +
        `(${s.tipus}, kötés: ${s.kotes ?? '—'})\n\n` +
        'Akkor töröld, ha tévesen került be. Ha valóban volt ilyen szerződés, inkább zárd le.\n' +
        'A törlés a naplóba kerül, minden adatával együtt.\n\nMi az oka? (nem kötelező)',
      'tévesen rögzítve',
    );
    if (indok === null) return;
    try {
      await api.torol(`/api/szerzodes/${s.id}?indok=${encodeURIComponent(indok)}`);
      setUzenet(`Törölve: ${s.szam ?? 'ideiglenes számú'} ${s.tipus} szerződés — ${s.nev}.`);
      await betolt();
    } catch (h) {
      setHiba(hibaSzoveg(h, 'A törlés nem sikerült.'));
    }
  }

  /** Kiküldött → aláírva megjött, illetve vissza. (2026-09-18) */
  async function allapotValtas(s: SzerzodesNezet, mire: 'alairva' | 'kikuldott') {
    try {
      // Külön írva, hogy a végpontok a kódból is kiolvashatók legyenek.
      if (mire === 'alairva') await api.post(`/api/szerzodes/${s.id}/alairva`);
      else await api.post(`/api/szerzodes/${s.id}/kikuldott`);
      setUzenet(
        mire === 'alairva'
          ? `${s.szam ?? s.nev}: az aláírt szerződés megérkezett — mostantól él.`
          : `${s.szam ?? s.nev}: visszatéve kiküldött állapotba.`,
      );
      await betolt();
    } catch (h) {
      setHiba(hibaSzoveg(h, 'Az állapot módosítása nem sikerült.'));
    }
  }

  async function kodokKiosztasa() {
    if (
      !confirm(
        'Ez minden éttermet ellát végleges partnerkóddal, abc sorrendben.\n' +
          'Éles indulás előtt futtasd, ne a teszt alatt. Folytatod?',
      )
    )
      return;
    try {
      const v = await api.post<{ etterem: number; szerzodes: number }>('/api/szerzodes/kodok-kiosztasa');
      setUzenet(
        `${v.etterem} étterem kapott partnerkódot` +
          (v.szerzodes > 0 ? `, ${v.szerzodes} szerződés megkapta a végleges számát.` : '.'),
      );
      await betolt();
    } catch (h) {
      setHiba(hibaSzoveg(h, 'A kiosztás nem sikerült.'));
    }
  }

  function kesz(uj: SzerzodesNezet, szoveg: string) {
    setUrlap(null);
    setUzenet(szoveg);
    setFul(uj.tipus);
    setSzuro(uj.helyszini ? 'helyszini' : 'mind');
  }

  function excel() {
    const p = new URLSearchParams({ fajta: ful, szuro });
    if (keresesK.trim()) p.set('q', keresesK.trim());
    letolt(`/api/export/szerzodesek.xlsx?${p}`);
  }

  const alapTipus: Tipus = ful === 'FO' ? 'FO' : 'MO';

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Szerződések</span>
        <div className="vsw">
          <button className={ful === 'AT' ? 'on' : ''} onClick={() => fulValtas('AT')}>
            Áttekintés
          </button>
          <button className={ful === 'MO' ? 'on' : ''} onClick={() => fulValtas('MO')}>
            MO — moslék
          </button>
          <button className={ful === 'FO' ? 'on' : ''} onClick={() => fulValtas('FO')}>
            FO / HSO
          </button>
        </div>
        <input
          className="qi"
          style={{ width: 240, fontSize: 12.5 }}
          placeholder="szám, étterem, cég, kód…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
        <div className="stats">
          <div>
            Érvényes<strong>{ft(o?.mo_elo ?? 0)}</strong>
          </div>
          <div>
            Hamarosan lejár<strong>{ft(o?.mo_hamarosan ?? 0)}</strong>
          </div>
          <div>
            Lejárt<strong>{ft(o?.mo_lejart ?? 0)}</strong>
          </div>
          <div>
            Hiányzó nyilatkozat<strong>{ful === 'MO' ? '—' : ft(o?.nyilatkozat_hianyzik ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="fchips">
        {Object.keys(adat?.szuro_nevek ?? {}).map((k) => (
          <button key={k} className={`fchip${szuro === k ? ' on' : ''}`} onClick={() => setSzuro(k)}>
            {adat?.szuro_nevek[k]}
            <b>{ft(adat?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {kuldendo && (
        <LevelKuldoPanel
          keres={kuldendo}
          onKesz={(u) => {
            setKuldendo(null);
            setUzenet(u);
          }}
          onMegse={() => setKuldendo(null)}
        />
      )}

      <div className="actbar">
        <button
          className="btn gold sm"
          onClick={() => setUrlap({ fajta: 'uj', tipus: alapTipus, etterem: null })}
        >
          + Új szerződés
        </button>
        <button className="btn sm" onClick={() => setUrlap({ fajta: 'helyszini', tipus: alapTipus })}>
          + Helyszíni szerződés
        </button>
        {letolthet && (
          <button className="btn sm" onClick={excel}>
            Excel
          </button>
        )}
        {adat && <KodSav kodok={adat.kodok} onKiosztas={() => void kodokKiosztasa()} />}
      </div>

      {urlap?.fajta === 'uj' && (
        <UjSzerzodes
          key={`${urlap.tipus}-${urlap.etterem?.id ?? 'x'}`}
          kezdoTipus={urlap.tipus}
          elore={urlap.etterem}
          onKesz={kesz}
          onHiba={setHiba}
          onMegse={() => setUrlap(null)}
        />
      )}
      {urlap?.fajta === 'helyszini' && (
        <HelyszinSzerzodes
          kezdoTipus={urlap.tipus}
          onKesz={kesz}
          onHiba={setHiba}
          onMegse={() => setUrlap(null)}
        />
      )}

      {ful === 'AT' && att?.kereses_teljes && (
        <div className="uzenet flag">
          A keresés a <b>teljes törzsben</b> ment, nem csak a kiválasztott szűrőn belül — így
          megtalálod azt is, akivel még nincs szerződés.
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        {ful === 'AT' ? (
          <AttekintesTabla
            sorok={att?.sorok ?? null}
            onKarton={onKarton}
            onUj={(tipus, s) => setUrlap({ fajta: 'uj', tipus, etterem: { id: s.etterem_id, nev: s.nev } })}
          />
        ) : (
          <SzerzodesTabla
            tipus={ful}
            sorok={lista?.sorok ?? null}
            onKarton={onKarton}
            onMent={ment}
            onNyomtat={(s) => void nyomtat(s)}
            onPdf={(s) => void pdf(s)}
            onKuldes={kuldhet ? (s) => void kuldesElokeszites(s) : undefined}
            onLezar={(s) => void lezar(s)}
            onAllapot={(s, mire) => void allapotValtas(s, mire)}
            onTorol={(s) => void torol(s)}
            onUjrakot={(s) =>
              s.etterem_id !== null &&
              setUrlap({ fajta: 'uj', tipus: s.tipus, etterem: { id: s.etterem_id, nev: s.nev } })
            }
          />
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function KodSav({ kodok, onKiosztas }: { kodok: KodAllapot; onKiosztas: () => void }) {
  if (kodok.kiosztva && kodok.kod_nelkul === 0) {
    return <span className="hint">Partnerkódok kiosztva · a szerződésszámok véglegesek</span>;
  }
  return (
    <span className="hint" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      {kodok.kiosztva ? (
        <span>
          Partnerkódok kiosztva — <b>{kodok.kod_nelkul}</b> étteremnek még nincs kódja.
        </span>
      ) : (
        <span>
          <b style={{ color: 'var(--flag)' }}>A partnerkódok még nincsenek kiosztva</b> — a
          szerződésszámok addig ideiglenesek.
        </span>
      )}
      <button className="btn sm" onClick={onKiosztas} title="Egyszeri művelet — éles induláskor futtasd">
        Kódok kiosztása
      </button>
    </span>
  );
}

/* --- Áttekintés: partnerenként egy sor ------------------------------ */

const csoportFej: React.CSSProperties = { textAlign: 'center', background: '#F1F3EF' };
const blokkEleje: React.CSSProperties = { borderLeft: '1px solid var(--rule2)' };

function AttekintesTabla({
  sorok,
  onKarton,
  onUj,
}: {
  sorok: SzerzAttekintesSor[] | null;
  onKarton?: (id: number) => void;
  onUj: (tipus: Tipus, s: SzerzAttekintesSor) => void;
}) {
  return (
    <table className="tt2">
      <thead>
        <tr>
          <th rowSpan={2} style={{ width: 46 }}>
            Kód
          </th>
          <th rowSpan={2}>Étterem</th>
          <th rowSpan={2}>Cég</th>
          <th colSpan={4} style={{ ...csoportFej, ...blokkEleje }}>
            MO — moslék, határozott idejű
          </th>
          <th colSpan={3} style={{ ...csoportFej, ...blokkEleje }}>
            FO / HSO — határozatlan
          </th>
          <th rowSpan={2} style={{ width: 118 }}></th>
        </tr>
        <tr>
          <th style={blokkEleje}>Szám</th>
          <th className="n">Lejárat</th>
          <th>Állapot</th>
          <th className="n">Ár</th>
          <th style={blokkEleje}>Szám</th>
          <th className="n">Kötés</th>
          <th>Nyil.</th>
        </tr>
      </thead>
      <tbody>
        {(sorok ?? []).map((s) => (
          <tr key={s.etterem_id} className={s.aktiv ? undefined : 'gyr'}>
            <td className="calc2" style={{ textAlign: 'left' }}>
              {s.kod ?? '—'}
            </td>
            <td
              className="nm3"
              onClick={onKarton ? () => onKarton(s.etterem_id) : undefined}
              title={onKarton ? 'A partnerkarton megnyitása' : undefined}
            >
              <b>{s.nev}</b>
              {s.cim && <small>{s.cim}</small>}
            </td>
            <td style={{ padding: '6px 10px', fontSize: 11.5 }}>{s.cegnev ?? '—'}</td>

            <td className="calc2" style={{ ...blokkEleje, textAlign: 'left' }}>
              {s.mo ? <SzamCella s={s.mo} /> : '—'}
            </td>
            <td className="calc2">{s.mo?.lejarat ?? '—'}</td>
            <td className={s.mo ? allapotOsztaly(s.mo) : undefined} style={{ padding: '6px 10px', fontSize: 11.5 }}>
              {s.mo ? allapotSzoveg(s.mo) : <span className="only">nincs</span>}
            </td>
            <td className="calc2">
              {s.mo_ar ? ft(s.mo_ar) : '—'}
              {s.mo_elteres_szazalek !== null && s.mo_elteres_szazalek !== 0 && (
                <div className="lejarat-alszoveg" style={{ color: 'var(--star)' }} title="Eltérés az alapártól">
                  {s.mo_elteres_szazalek > 0 ? '+' : ''}
                  {s.mo_elteres_szazalek}%
                </div>
              )}
            </td>

            <td className="calc2" style={{ ...blokkEleje, textAlign: 'left' }}>
              {s.fo ? <SzamCella s={s.fo} /> : '—'}
            </td>
            <td className="calc2">{s.fo?.kotes ?? '—'}</td>
            <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
              {s.fo ? s.fo.nyilatkozat ? 'van' : <span className="only">hiányzik</span> : '—'}
            </td>

            <td className="act" style={{ whiteSpace: 'nowrap' }}>
              <button
                className="btn sm"
                title={s.mo ? 'MO szerződés újrakötése' : 'Új MO szerződés'}
                onClick={() => onUj('MO', s)}
              >
                MO {s.mo ? '↻' : '+'}
              </button>{' '}
              <button
                className="btn sm"
                title={s.fo ? 'FO szerződés újrakötése' : 'Új FO szerződés'}
                onClick={() => onUj('FO', s)}
              >
                FO {s.fo ? '↻' : '+'}
              </button>
            </td>
          </tr>
        ))}
        {(sorok ?? []).length === 0 && (
          <tr>
            <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
              {sorok === null ? 'Betöltés…' : 'Nincs találat.'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* --- MO és FO fül: szerződésenként egy sor -------------------------- */

function SzerzodesTabla({
  tipus,
  sorok,
  onKarton,
  onMent,
  onNyomtat,
  onPdf,
  onKuldes,
  onUjrakot,
  onLezar,
  onAllapot,
  onTorol,
}: {
  tipus: Tipus;
  sorok: SzerzodesNezet[] | null;
  onKarton?: (id: number) => void;
  onMent: (id: number, mezo: string, ertek: unknown) => Promise<void>;
  onNyomtat: (s: SzerzodesNezet) => void;
  onPdf: (s: SzerzodesNezet) => void;
  /** Csak akkor van gomb, ha a felhasználó küldhet levelet. */
  onKuldes?: (s: SzerzodesNezet) => void;
  onUjrakot: (s: SzerzodesNezet) => void;
  onLezar: (s: SzerzodesNezet) => void;
  onAllapot: (s: SzerzodesNezet, mire: 'alairva' | 'kikuldott') => void;
  onTorol: (s: SzerzodesNezet) => void;
}) {
  const mo = tipus === 'MO';
  return (
    <table className="tt2">
      <thead>
        <tr>
          <th>Szerződésszám</th>
          <th style={{ width: 46 }}>Kód</th>
          <th>Étterem</th>
          <th>Cég</th>
          <th className="n" style={{ width: 96 }}>
            Kötés
          </th>
          {mo && (
            <th className="n" style={{ width: 96 }}>
              Lejárat
            </th>
          )}
          <th style={{ width: 128 }}>Állapot</th>
          <th className="n" style={{ width: 56 }}>
            {mo ? 'Hordó' : 'Edény'}
          </th>
          <th className="n" style={{ width: 80 }}>
            {mo ? 'MO ár' : 'Ár'}
          </th>
          {!mo && <th style={{ width: 44 }}>Nyil.</th>}
          <th>Megjegyzés</th>
          <th style={{ width: 260 }}></th>
        </tr>
      </thead>
      <tbody>
        {(sorok ?? []).map((s) => {
          const halvany = s.allapot === 'ujra' || s.allapot === 'lezart';
          const ment = (mezo: string) => (v: unknown) => onMent(s.id, mezo, v);
          return (
            <tr key={s.id} className={halvany ? 'gyr' : undefined}>
              <td
                className="nm3"
                onClick={onKarton && s.etterem_id !== null ? () => onKarton(s.etterem_id!) : undefined}
              >
                <b>
                  <SzamCella s={s} />
                </b>
              </td>
              <td className="calc2" style={{ textAlign: 'left' }}>
                {s.kod ?? '—'}
              </td>
              <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                {s.nev}
                {s.helyszini && (
                  <span className="tag" style={{ marginLeft: 6 }}>
                    helyszíni
                  </span>
                )}
              </td>
              <td style={{ padding: '6px 10px', fontSize: 11.5 }}>{s.cegnev ?? '—'}</td>
              <td>
                <Mezo ertek={s.kotes} onMent={ment('kotes')} osztaly="c" cim="ÉÉÉÉ-HH-NN" />
              </td>
              {mo && (
                <td>
                  <Mezo ertek={s.lejarat} onMent={ment('lejarat')} osztaly="c" cim="ÉÉÉÉ-HH-NN" />
                </td>
              )}
              <td className={allapotOsztaly(s)} style={{ padding: '6px 10px', fontSize: 11.5 }}>
                {allapotSzoveg(s)}
              </td>
              <td>
                <SzamMezo ertek={mo ? s.hordo_db : s.edeny_db} onMent={ment(mo ? 'hordo_db' : 'edeny_db')} />
              </td>
              {mo ? (
                <td className="calc2" title="A partner moslékára — a Partnereknél szerkeszthető">
                  {s.mo_ar ? ft(s.mo_ar) : '—'}
                </td>
              ) : (
                <td>
                  <SzamMezo ertek={s.ar} onMent={ment('ar')} cim="A szerződésbe írt HSO ár (Ft/kg, nettó)" />
                </td>
              )}
              {!mo && (
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={s.nyilatkozat}
                    title="Megvan-e a partner nyilatkozata"
                    onChange={(e) => void onMent(s.id, 'nyilatkozat', e.target.checked)}
                  />
                </td>
              )}
              <td>
                <Mezo ertek={s.megjegyzes} onMent={ment('megjegyzes')} helykitolto="—" />
              </td>
              <td className="act" style={{ whiteSpace: 'nowrap' }}>
                {onKuldes && (
                  <button
                    className="btn sm"
                    title="A szerződés elküldése e-mailben"
                    onClick={() => onKuldes(s)}
                  >
                    Küldés
                  </button>
                )}
                <button className="btn sm" onClick={() => onNyomtat(s)}>
                  Nyomtat
                </button>{' '}
                {/* Ha a nyomtató a böngészőből nyomtatva akadozik. (2026-10-06) */}
                <button
                  className="btn sm"
                  title="A szerződés és a nyilatkozat PDF-ben — ha a nyomtató a böngészőből nyomtatva hibázik"
                  onClick={() => onPdf(s)}
                >
                  PDF
                </button>
                {s.allapot === 'kikuldott' && (
                  <>
                    {' '}
                    <button
                      className="btn sm pri"
                      title="Az aláírt példány visszaérkezett — a szerződés mostantól él"
                      onClick={() => onAllapot(s, 'alairva')}
                    >
                      Aláírva megjött
                    </button>
                  </>
                )}
                {s.allapot === 'elo' && (
                  <>
                    {' '}
                    <button
                      className="btn sm"
                      title="Mégis kiküldött állapotba teszi — ha tévedésből lett élő"
                      onClick={() => onAllapot(s, 'kikuldott')}
                    >
                      Kiküldött
                    </button>
                  </>
                )}
                {s.allapot === 'lejart' && s.etterem_id !== null && (
                  <>
                    {' '}
                    <button className="btn sm" onClick={() => onUjrakot(s)}>
                      Újraköt
                    </button>
                  </>
                )}
                {!halvany && (
                  <>
                    {' '}
                    <button
                      className="btn sm"
                      title="Lezárás indoklással — a Sales listájába kerül"
                      onClick={() => onLezar(s)}
                    >
                      lezár
                    </button>
                  </>
                )}{' '}
                <button
                  className="btn sm torlo"
                  title="Tévesen rögzített szerződés törlése — a napló megőrzi az adatait"
                  onClick={() => onTorol(s)}
                >
                  Töröl
                </button>
              </td>
            </tr>
          );
        })}
        {(sorok ?? []).length === 0 && (
          <tr>
            <td colSpan={12} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
              {sorok === null ? 'Betöltés…' : 'Nincs ilyen szerződés.'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* --- új szerződés --------------------------------------------------- */

function SzamElonezet({ tipus, etterem_id, kotes, helyszini }: {
  tipus: Tipus;
  etterem_id: number | null;
  kotes: string;
  helyszini?: boolean;
}) {
  const [e, setE] = useState<{ szam: string | null; ideiglenes: boolean } | null>(null);
  useEffect(() => {
    if (!helyszini && etterem_id === null) {
      setE(null);
      return;
    }
    const p = new URLSearchParams({ tipus, kotes });
    if (etterem_id !== null) p.set('etterem_id', String(etterem_id));
    if (helyszini) p.set('helyszini', '1');
    let el = false;
    api
      .get<{ szam: string | null; ideiglenes: boolean }>(`/api/szerzodes/szam-elonezet?${p}`)
      .then((v) => !el && setE(v))
      .catch(() => !el && setE(null));
    return () => {
      el = true;
    };
  }, [tipus, etterem_id, kotes, helyszini]);

  if (!helyszini && etterem_id === null) return <span className="hint">Válassz éttermet.</span>;
  if (e?.ideiglenes) {
    return (
      <span className="hint" style={{ color: 'var(--flag)' }}>
        A partnernek még nincs kódja — a szám az éles betöltés után lesz végleges.
      </span>
    );
  }
  return (
    <span className="hint">
      Szerződésszám: <b style={{ fontFamily: 'var(--mono)' }}>{e?.szam ?? '…'}</b>
    </span>
  );
}

function TipusValaszto({ tipus, onValt }: { tipus: Tipus; onValt: (t: Tipus) => void }) {
  return (
    <div className="vsw" style={{ display: 'inline-flex' }}>
      <button className={tipus === 'MO' ? 'on' : ''} onClick={() => onValt('MO')}>
        MO — moslék
      </button>
      <button className={tipus === 'FO' ? 'on' : ''} onClick={() => onValt('FO')}>
        FO / HSO
      </button>
    </div>
  );
}

function UjSzerzodes({
  kezdoTipus,
  elore,
  onKesz,
  onHiba,
  onMegse,
}: {
  kezdoTipus: Tipus;
  elore: { id: number; nev: string } | null;
  onKesz: (uj: SzerzodesNezet, szoveg: string) => void;
  onHiba: (h: string) => void;
  onMegse: () => void;
}) {
  const [tipus, setTipus] = useState<Tipus>(kezdoTipus);
  const [etterem, setEtterem] = useState<{ id: number; nev: string } | null>(elore);
  const [q, setQ] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [kotes, setKotes] = useState(ma());
  const [ev, setEv] = useState('2');
  const [darab, setDarab] = useState('2');
  const [ar, setAr] = useState('');
  const [keziSzam, setKeziSzam] = useState('');
  /** Meglévő szerződés rögzítésénél: aláírva megvan-e már. (2026-09-18) */
  const [alairt, setAlairt] = useState(true);
  const [munkaban, setMunkaban] = useState(false);

  useEffect(() => {
    const k = q.trim();
    if (k.length < 2) {
      setTalalatok([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const v = await api.get<{ sorok: EtteremSor[] }>(`/api/etterem?q=${encodeURIComponent(k)}&limit=7&csak_aktiv=1`);
        setTalalatok(v.sorok);
      } catch {
        setTalalatok([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  /**
   * Létrehozás. (2026-09-18)
   *
   *  - **Generálás:** elkészül, megnyílik nyomtatásra, és „kiküldve, aláírásra
   *    vár" állapotba kerül. Aláírva visszaérkezéskor kell átállítani.
   *  - **Rögzítés:** már meglévő, kint lévő szerződés felvétele nyomtatás
   *    nélkül. Ilyenkor lehet aláírtnak (élő) vagy kiküldöttnek jelölni.
   */
  async function letrehoz(rogzites: boolean) {
    if (!etterem) return;
    // Az ablakot még a kattintásban nyitjuk meg — a válasz után a böngésző letiltaná.
    const ablak = rogzites ? null : ablakotNyit(SZERZODES_CSS);
    setMunkaban(true);
    try {
      const uj = await api.post<SzerzodesNezet>('/api/szerzodes', {
        tipus,
        etterem_id: etterem.id,
        kotes,
        ev: Number(ev) || 2,
        darab: darab.trim() ? Number(darab) : null,
        ar: tipus === 'FO' && ar.trim() ? Number(ar.replace(',', '.')) : null,
        szam: keziSzam.trim() || null,
        alairt: rogzites && alairt,
      });
      if (ablak) {
        await szerzodesNyomtatas(uj.id, ablak).catch((h: unknown) =>
          onHiba(`A szerződés létrejött, de a nyomtatás nem sikerült: ${hibaSzoveg(h, '')}`),
        );
      }
      onKesz(
        uj,
        `${rogzites ? 'Rögzítve' : 'Létrejött'}: ${uj.szam ?? 'ideiglenes számú'} ${tipus} szerződés — ${uj.nev}` +
          (uj.allapot === 'kikuldott' ? ' · kiküldve, aláírásra vár.' : ' · él.'),
      );
    } catch (h) {
      if (ablak) ablakotBezar(ablak);
      onHiba(hibaSzoveg(h, 'A szerződés nem jött létre.'));
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4 style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        Új {tipus} szerződés <TipusValaszto tipus={tipus} onValt={setTipus} />
      </h4>

      {etterem ? (
        <p style={{ margin: '0 0 8px' }}>
          Étterem: <b>{etterem.nev}</b>{' '}
          <button className="btn sm" onClick={() => setEtterem(null)}>
            másik
          </button>
        </p>
      ) : (
        <>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="étterem neve, cégnév vagy kód — legalább két betű"
            style={{ width: '100%', fontFamily: 'inherit', textAlign: 'left' }}
          />
          {q.trim().length >= 2 && (
            <div className="kereso-talalatok">
              {talalatok.map((t) => (
                <div
                  key={t.id}
                  className="kereso-sor"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setEtterem({ id: t.id, nev: t.nev })}
                >
                  <div className="kereso-nev">
                    <b>
                      {t.kod && <span style={{ fontFamily: 'var(--mono)', marginRight: 6 }}>{t.kod}</span>}
                      {t.nev}
                    </b>
                    <small>
                      {[[t.irsz, t.cim].filter(Boolean).join(' '), t.cegnev].filter(Boolean).join(' · ') ||
                        'nincs cím megadva'}
                    </small>
                  </div>
                </div>
              ))}
              {talalatok.length === 0 && <div className="kereso-ures">Nincs ilyen partner a törzsben.</div>}
            </div>
          )}
        </>
      )}

      <div className="qgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: 8 }}>
        <label>
          Kötés dátuma
          <input value={kotes} onChange={(e) => setKotes(e.target.value)} placeholder="ÉÉÉÉ-HH-NN" />
        </label>
        {tipus === 'MO' ? (
          <label>
            Időtartam (év)
            <input value={ev} onChange={(e) => setEv(e.target.value)} />
          </label>
        ) : (
          <label>
            Időtartam
            <input value="határozatlan" disabled />
          </label>
        )}
        <label>
          {tipus === 'MO' ? 'Hordó darabszám' : 'Gyűjtőedény (db)'}
          <input value={darab} onChange={(e) => setDarab(e.target.value)} />
        </label>
        {tipus === 'FO' ? (
          <label>
            HSO ár (Ft/kg)
            <input value={ar} onChange={(e) => setAr(e.target.value)} placeholder="Termékek szerint" />
          </label>
        ) : (
          <label>
            Szerződésszám (kézi)
            <input value={keziSzam} onChange={(e) => setKeziSzam(e.target.value)} placeholder="automatikus" />
          </label>
        )}
      </div>
      {tipus === 'FO' && (
        <div className="qgrid" style={{ gridTemplateColumns: '1fr 3fr' }}>
          <label>
            Szerződésszám (kézi)
            <input value={keziSzam} onChange={(e) => setKeziSzam(e.target.value)} placeholder="automatikus" />
          </label>
        </div>
      )}

      <div className="btns" style={{ alignItems: 'center' }}>
        <button className="btn gold sm" disabled={!etterem || munkaban} onClick={() => void letrehoz(false)}>
          Létrehozás és nyomtatás
        </button>
        <span className="hint">kiküldve, aláírásra vár</span>
        <span style={{ width: 12 }} />
        <button
          className="btn sm"
          disabled={!etterem || munkaban}
          title="Már meglévő szerződés felvétele nyomtatás nélkül — például ami kint van a partnernél"
          onClick={() => void letrehoz(true)}
        >
          Meglévő rögzítése
        </button>
        <label className="sw" title="Ha az aláírt példány már megvan, a szerződés rögtön él.">
          <input type="checkbox" checked={alairt} onChange={(e) => setAlairt(e.target.checked)} />
          aláírva megvan
        </label>
        <span style={{ width: 12 }} />
        <button className="btn sm" onClick={onMegse}>
          Mégse
        </button>
        {keziSzam.trim() ? (
          <span className="hint">Kézzel megadott szám: {keziSzam.trim()}</span>
        ) : (
          <SzamElonezet tipus={tipus} etterem_id={etterem?.id ?? null} kotes={kotes} />
        )}
      </div>
    </div>
  );
}

/* --- helyszíni szerződés -------------------------------------------- */

function HelyszinSzerzodes({
  kezdoTipus,
  onKesz,
  onHiba,
  onMegse,
}: {
  kezdoTipus: Tipus;
  onKesz: (uj: SzerzodesNezet, szoveg: string) => void;
  onHiba: (h: string) => void;
  onMegse: () => void;
}) {
  const [tipus, setTipus] = useState<Tipus>(kezdoTipus);
  const [nev, setNev] = useState('');
  const [ceg, setCeg] = useState('');
  const [adoszam, setAdoszam] = useState('');
  const [cim, setCim] = useState('');
  const [kotes, setKotes] = useState(ma());
  const [darab, setDarab] = useState('2');
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  async function letrehoz() {
    if (!nev.trim()) {
      setHiba('A megnevezés kötelező.');
      return;
    }
    const ablak = ablakotNyit(SZERZODES_CSS);
    setMunkaban(true);
    try {
      const uj = await api.post<SzerzodesNezet>('/api/szerzodes', {
        tipus,
        kotes,
        darab: darab.trim() ? Number(darab) : null,
        helyszini: { nev: nev.trim(), ceg: ceg.trim() || null, adoszam: adoszam.trim() || null, cim: cim.trim() || null },
      });
      await szerzodesNyomtatas(uj.id, ablak).catch((h: unknown) =>
        onHiba(`A szerződés létrejött, de a nyomtatás nem sikerült: ${hibaSzoveg(h, '')}`),
      );
      onKesz(uj, `Létrejött a helyszíni szerződés: ${uj.szam} — ${uj.nev}.`);
    } catch (h) {
      ablakotBezar(ablak);
      onHiba(hibaSzoveg(h, 'A szerződés nem jött létre.'));
      setMunkaban(false);
    }
  }

  const bal = { textAlign: 'left' as const, fontFamily: 'inherit' };

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4 style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        Helyszíni {tipus} szerződés <TipusValaszto tipus={tipus} onValt={setTipus} />
      </h4>
      <p className="hint" style={{ margin: '0 0 8px' }}>
        Olyan új ügyfélhez, aki még nincs a törzsben. Külön sorszámot kap; ha később felvesszük
        partnerként, onnantól a rendes számozás megy.
      </p>
      <div className="qgrid" style={{ gridTemplateColumns: '2fr 2fr 1fr' }}>
        <label>
          Étterem / egység neve *
          <input value={nev} onChange={(e) => setNev(e.target.value)} style={bal} />
        </label>
        <label>
          Cégnév
          <input value={ceg} onChange={(e) => setCeg(e.target.value)} style={bal} />
        </label>
        <label>
          Adószám
          <input value={adoszam} onChange={(e) => setAdoszam(e.target.value)} placeholder="12345678-2-42" />
        </label>
      </div>
      <div className="qgrid" style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr' }}>
        <label>
          Cím
          <input value={cim} onChange={(e) => setCim(e.target.value)} style={bal} />
        </label>
        <label>
          Kötés dátuma
          <input value={kotes} onChange={(e) => setKotes(e.target.value)} />
        </label>
        <label>
          Időtartam
          <input value={tipus === 'MO' ? '2 év' : 'határozatlan'} disabled />
        </label>
        <label>
          {tipus === 'MO' ? 'Hordó db' : 'Gyűjtőedény db'}
          <input value={darab} onChange={(e) => setDarab(e.target.value)} />
        </label>
      </div>
      {hiba && <div className="uzenet bad">{hiba}</div>}
      <div className="btns">
        <button className="btn gold sm" disabled={munkaban} onClick={() => void letrehoz()}>
          Létrehozás
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégse
        </button>
        <SzamElonezet tipus={tipus} etterem_id={null} kotes={kotes} helyszini />
      </div>
    </div>
  );
}
